// Хук доставки обновлений веб-слоя: запускает проверку и отдаёт баннеру состояние.
//
// Монтируется ОДИН раз — в защищённом слое (AppLayout), рядом с двигателем очереди
// синка, по той же причине: это приложение-широкая забота, и владелец у неё один.

import { useEffect } from 'react';

import { useOnlineStatus } from '@/shared/hooks/useOnlineStatus';

import { useAppUpdateStore } from './appUpdateStore';
import { checkForUpdate } from './checkForUpdate';
import { applyBundle, updatesSupported } from './updater';

export type AppUpdate = {
  /** Готов ли скачанный бандл к применению — единственный вход для баннера. */
  readonly ready: boolean;
  /** Версия готового бандла (для текста баннера). */
  readonly version: string | null;
  /** Идёт применение — кнопку в баннере на это время дизейблим. */
  readonly applying: boolean;
  /** Применить обновление и перезагрузить webview. Вызывать только по клику. */
  readonly apply: () => void;
};

export function useAppUpdate(): AppUpdate {
  const online = useOnlineStatus();
  const phase = useAppUpdateStore((state) => state.phase);
  const version = useAppUpdateStore((state) => state.readyVersion);
  const bundleId = useAppUpdateStore((state) => state.bundleId);
  const setPhase = useAppUpdateStore((state) => state.setPhase);

  // Проверка при монтировании и при каждом возврате сети: обновление могло выйти,
  // пока планшет был офлайн.
  useEffect(() => {
    if (!updatesSupported() || !online) return;
    void checkForUpdate();
  }, [online]);

  const apply = (): void => {
    if (!bundleId) return;
    setPhase('applying');
    // Ошибку применения не гасим в UI: если set/reload не прошёл, webview останется
    // на прежнем бандле — это безопасно, а в консоли будет след.
    void applyBundle(bundleId).catch((error) => {
      console.error('Применение обновления не удалось', error);
      setPhase('ready');
    });
  };

  return {
    ready: phase === 'ready',
    version,
    applying: phase === 'applying',
    apply,
  };
}
